import { resolveOtherInfoObj } from '@moko/utils';
import { useEffect, useRef, useState } from 'react';
import { IMetadata, getMetadata } from '../../services/metadataService';
import { IGroup } from '../../services/groupService';
import {
  ICreateRenterRequest,
  IUpdateRenterRequest,
  createRenter,
  editRenter,
  getRenter,
} from '../../services/renterService';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useLocation } from 'react-router-dom';
import UserOrGroupSelector from '../../common/selectors/UsersOrGroupSelectors';
import { EditItem } from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';

const Renter = () => {
  const query = new URLSearchParams(useLocation().search);
  const itemId = Number.parseInt(query.get('id') ?? '');

  const [metadata, setMetadata] = useState<IMetadata>();
  const [otherInfo, setOtherInfo] = useState({});

  const [_renterId, setRenterId] = useState<number>(itemId);
  const [_group, setGroup] = useState<IGroup>();

  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [
    isUserSelectorOpen,
    { setTrue: _showUserSelector, setFalse: hideUserSelector },
  ] = useBoolean(false);

  const isSaved = useRef(false);

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const metadata = await getMetadata(abortController, 'Renter');
      setMetadata(metadata);
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <EditItem
        getAction={async (abortController, id) => {
          const renter: any = await getRenter(abortController, id);
          setRenterId(id);
          setOtherInfo(resolveOtherInfoObj(renter.otherInfo) ?? {});
          renter.statusId = (renter.statusId ?? '').toString();
          return renter;
        }}
        newAction={async (abortController, newWork) => {
          const data: ICreateRenterRequest = {
            code: newWork.code as string,
            name: newWork.name as string,
            otherInfo: JSON.stringify(otherInfo),
          };
          const newRenterResponse = await createRenter(abortController, data);
          setSuccessMessage(
            `Renter with id: ${newRenterResponse.renterId} created.`
          );
        }}
        editAction={async (abortController, id, editedWork) => {
          const data: IUpdateRenterRequest = {
            name: editedWork.name as string,
            code: editedWork.code as string,
            otherInfo: JSON.stringify(otherInfo),
          };
          await editRenter(abortController, id, data);
          setSuccessMessage(`Renter with id: ${id} successfully updated.`);
        }}
        isSaved={isSaved}
        back={'/renters'}
        metadata={{
          fields: [
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
            },
            {
              name: 'code',
              fieldType: 'String',
              label: 'Code',
            },
          ],
          lookups: [],
          validations: [],
          interactions: [],
        }}
      >
        <>
          <Stack horizontal>
            <UserOrGroupSelector
              userOrGroupOnly={'group'}
              isOpen={isUserSelectorOpen}
              onUserOrGroupSelected={(
                id: number,
                _isGroup: boolean,
                username?: string
              ) => {
                setGroup({
                  id,
                  name: username ?? '',
                } as IGroup);
              }}
              hideUsersAndGroupsSelector={hideUserSelector}
            />
          </Stack>

          {metadata ? (
            <OtherInfo
              metadata={metadata}
              otherInfo={JSON.stringify(otherInfo)}
              isSaved={isSaved}
              onOtherInfoChanged={(newValue: string) => setOtherInfo(newValue)}
            />
          ) : (
            <></>
          )}
        </>
      </EditItem>
    </>
  );
};

export default Renter;
